import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { COLORS, TYPOGRAPHY, SPACING, RADIUS, SHADOWS } from '../designSystem';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { useBackNavigation, createBackButton } from '../hooks/useBackNavigation';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

export default function FunderCreate() {
  const navigate = useNavigate();
  const { setHeaderConfig, resetHeaderConfig } = usePageHeader();
  const handleBack = useBackNavigation('list', '/funders');

  const [formData, setFormData] = useState({
    legal_name: '',
    display_name: '',
    abbreviation: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setHeaderConfig({
      backButton: createBackButton(handleBack),
      title: 'New Funder',
      subtitle: null,
      actions: [],
    });
    return () => resetHeaderConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setHeaderConfig, resetHeaderConfig]);

  const stateOptions = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/funding-entities`, formData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const newFunderId = response.data.id;
      navigate(`/funders/${newFunderId}`, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create funder');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.pageWrapper}>
      <div style={styles.container}>
        <form onSubmit={handleSubmit} style={styles.form}>
          {error && <div style={styles.error}>{error}</div>}

          <div style={styles.formSection}>
            <h2 style={styles.sectionTitle}>Funder Information</h2>

            <div style={styles.gridTwo}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Legal Name *</label>
                <input
                  type="text"
                  name="legal_name"
                  value={formData.legal_name}
                  onChange={handleChange}
                  required
                  style={styles.input}
                  placeholder="Full legal company name"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>DBA / Display Name</label>
                <input
                  type="text"
                  name="display_name"
                  value={formData.display_name}
                  onChange={handleChange}
                  style={styles.input}
                  placeholder="How the funder appears in lists"
                />
              </div>
            </div>

            <div style={styles.gridTwo}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Abbreviation</label>
                <input
                  type="text"
                  name="abbreviation"
                  value={formData.abbreviation}
                  onChange={handleChange}
                  style={styles.input}
                  placeholder="e.g., MCG"
                  maxLength="5"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  style={styles.input}
                  placeholder="(555) 000-0000"
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                style={styles.input}
                placeholder="contact@funder.com"
              />
            </div>
          </div>

          <div style={styles.formSection}>
            <h2 style={styles.sectionTitle}>Address</h2>

            <div style={styles.formGroup}>
              <label style={styles.label}>Street Address</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                style={styles.input}
                placeholder="Street address"
              />
            </div>

            <div style={styles.gridThree}>
              <div style={styles.formGroup}>
                <label style={styles.label}>City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  style={styles.input}
                  placeholder="City"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>State</label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  style={styles.select}
                >
                  <option value="">Select state</option>
                  {stateOptions.map((state) => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>ZIP Code</label>
                <input
                  type="text"
                  name="zip"
                  value={formData.zip}
                  onChange={handleChange}
                  style={styles.input}
                  placeholder="12345"
                />
              </div>
            </div>
          </div>

          <div style={styles.formActions}>
            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.submitButton,
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? 'Creating...' : 'Create Funder'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/funders')}
              style={styles.cancelButton}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  pageWrapper: {
    backgroundColor: COLORS.BG_PAGE_SURFACE,
    minHeight: '100vh',
    paddingTop: SPACING.XL,
    paddingBottom: SPACING.XXXL,
  },

  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: `0 ${SPACING.XL}`,
  },

  form: {
    backgroundColor: COLORS.BG_SURFACE_PRIMARY,
    borderRadius: RADIUS.DEFAULT,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    boxShadow: SHADOWS.DEFAULT,
    padding: SPACING.XXXL,
  },

  error: {
    padding: SPACING.LG,
    backgroundColor: '#fee2e2',
    border: `1px solid #fca5a5`,
    borderRadius: RADIUS.DEFAULT,
    color: COLORS.ERROR,
    marginBottom: SPACING.XL,
    fontSize: TYPOGRAPHY.BODY.fontSize,
  },

  formSection: {
    marginBottom: SPACING.XXXL,

    '&:last-child': {
      marginBottom: 0,
    },
  },

  sectionTitle: {
    ...TYPOGRAPHY.H3,
    color: COLORS.TEXT_PRIMARY,
    margin: `0 0 ${SPACING.LG} 0`,
    paddingBottom: SPACING.MD,
    borderBottom: `1px solid ${COLORS.BORDER_LIGHT}`,
  },

  formGroup: {
    marginBottom: SPACING.LG,
  },

  label: {
    display: 'block',
    ...TYPOGRAPHY.BODY,
    fontWeight: '600',
    color: COLORS.TEXT_PRIMARY,
    marginBottom: SPACING.SM,
  },

  input: {
    width: '100%',
    padding: `${SPACING.MD} ${SPACING.LG}`,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    color: COLORS.TEXT_PRIMARY,
    backgroundColor: COLORS.BG_WHITE,
    outline: 'none',
    transition: 'border-color 0.2s ease',

    '&:focus': {
      borderColor: COLORS.PRIMARY,
    },
  },

  select: {
    width: '100%',
    padding: `${SPACING.MD} ${SPACING.LG}`,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontFamily: TYPOGRAPHY.FONT_FAMILY_BODY,
    color: COLORS.TEXT_PRIMARY,
    backgroundColor: COLORS.BG_WHITE,
    outline: 'none',
    transition: 'border-color 0.2s ease',
  },

  gridTwo: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: SPACING.LG,
  },

  gridThree: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: SPACING.LG,
  },

  formActions: {
    display: 'flex',
    gap: SPACING.MD,
    justifyContent: 'flex-end',
    marginTop: SPACING.XXXL,
    paddingTop: SPACING.LG,
    borderTop: `1px solid ${COLORS.BORDER_LIGHT}`,
  },

  submitButton: {
    padding: `${SPACING.MD} ${SPACING.XXL}`,
    backgroundColor: COLORS.PRIMARY_DARK,
    color: COLORS.TEXT_WHITE,
    border: 'none',
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(77, 219, 148, 0.3)',
  },

  cancelButton: {
    padding: `${SPACING.MD} ${SPACING.XXL}`,
    backgroundColor: COLORS.BG_SURFACE_SECONDARY,
    color: COLORS.TEXT_PRIMARY,
    border: `1px solid ${COLORS.BORDER_LIGHT}`,
    borderRadius: RADIUS.DEFAULT,
    fontSize: TYPOGRAPHY.BODY.fontSize,
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
};
